import { type Component, Container, getKeybindings, Spacer, Text, truncateToWidth } from "@elyracode/tui";
import { theme } from "../theme/theme.js";
import { DynamicBorder } from "./dynamic-border.js";

/** A rewind point shown in the selector. */
export interface RewindPoint {
	/** Session entry ID for this rewind point. */
	entryId: string;
	/** Human-readable description of the state at this point. */
	text: string;
	/** ISO timestamp. */
	timestamp: string;
	/** Number of files that will change if rewinding to this point. */
	filesChanged: number;
	/** Whether this is the current position in the session. */
	isCurrent: boolean;
}

/**
 * List component for navigating rewind points.
 */
class RewindList implements Component {
	private items: RewindPoint[] = [];
	private selectedIndex = 0;
	private maxVisible = 10;

	public onSelect?: (entryId: string) => void;
	public onCancel?: () => void;

	constructor(items: RewindPoint[], initialSelectedIndex?: number) {
		this.items = items;
		this.selectedIndex = initialSelectedIndex ?? Math.max(0, items.length - 1);
	}

	invalidate(): void {
		// No cached state
	}

	render(width: number): string[] {
		const lines: string[] = [];

		if (this.items.length === 0) {
			lines.push(theme.fg("muted", "  No rewind points available"));
			return lines;
		}

		// Calculate visible range with scrolling
		const startIndex = Math.max(
			0,
			Math.min(this.selectedIndex - Math.floor(this.maxVisible / 2), this.items.length - this.maxVisible),
		);
		const endIndex = Math.min(startIndex + this.maxVisible, this.items.length);

		for (let i = startIndex; i < endIndex; i++) {
			const item = this.items[i];
			const isSelected = i === this.selectedIndex;

			// First line: cursor + description
			const cursor = isSelected ? theme.fg("accent", "› ") : "  ";
			const normalizedText = item.text.replace(/\n/g, " ").trim();
			const maxWidth = width - 2;
			const truncatedText = truncateToWidth(normalizedText, maxWidth);
			const styledText = item.isCurrent
				? theme.fg("muted", `${truncatedText} (current)`)
				: isSelected
					? theme.bold(truncatedText)
					: truncatedText;
			lines.push(cursor + styledText);

			// Second line: metadata
			const time = formatTimestamp(item.timestamp);
			const fileInfo = item.isCurrent
				? ""
				: item.filesChanged === 1
					? " · 1 file will change"
					: item.filesChanged > 1
						? ` · ${item.filesChanged} files will change`
						: "";
			lines.push(theme.fg("muted", `  ${time}${fileInfo}`));
			lines.push(""); // spacer
		}

		// Scroll indicator
		if (startIndex > 0 || endIndex < this.items.length) {
			lines.push(theme.fg("muted", `  (${this.selectedIndex + 1}/${this.items.length})`));
		}

		return lines;
	}

	handleInput(keyData: string): void {
		const kb = getKeybindings();
		if (kb.matches(keyData, "tui.select.up")) {
			this.selectedIndex = this.selectedIndex === 0 ? this.items.length - 1 : this.selectedIndex - 1;
		} else if (kb.matches(keyData, "tui.select.down")) {
			this.selectedIndex = this.selectedIndex === this.items.length - 1 ? 0 : this.selectedIndex + 1;
		} else if (kb.matches(keyData, "tui.select.confirm")) {
			const selected = this.items[this.selectedIndex];
			if (selected && !selected.isCurrent && this.onSelect) {
				this.onSelect(selected.entryId);
			}
		} else if (kb.matches(keyData, "tui.select.cancel")) {
			this.onCancel?.();
		}
	}
}

/**
 * Container component for the rewind selector UI.
 */
export class RewindSelectorComponent extends Container {
	private rewindList: RewindList;

	constructor(
		items: RewindPoint[],
		onSelect: (entryId: string) => void,
		onCancel: () => void,
		initialSelectedIndex?: number,
	) {
		super();

		this.addChild(new Spacer(1));
		this.addChild(new Text(theme.bold("Rewind Session"), 1, 0));
		this.addChild(
			new Text(theme.fg("muted", "Select a point to rewind to — restores both conversation and file state"), 1, 0),
		);
		this.addChild(new Spacer(1));
		this.addChild(new DynamicBorder());
		this.addChild(new Spacer(1));

		this.rewindList = new RewindList(items, initialSelectedIndex);
		this.rewindList.onSelect = onSelect;
		this.rewindList.onCancel = onCancel;
		this.addChild(this.rewindList);

		this.addChild(new Spacer(1));
		this.addChild(new DynamicBorder());

		if (items.length === 0) {
			setTimeout(() => onCancel(), 100);
		}
	}

	getList(): RewindList {
		return this.rewindList;
	}
}

/** Format an ISO timestamp to a human-readable relative or short form. */
function formatTimestamp(iso: string): string {
	const date = new Date(iso);
	const now = Date.now();
	const diffMs = now - date.getTime();
	const diffMin = Math.floor(diffMs / 60_000);

	if (diffMin < 1) return "just now";
	if (diffMin < 60) return `${diffMin}m ago`;
	const diffHours = Math.floor(diffMin / 60);
	if (diffHours < 24) return `${diffHours}h ago`;
	return date.toLocaleDateString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}
